// src/lib/stereo70.ts — WGS84 (EPSG:4326) ↔ Stereo 70 (EPSG:3844), PUR, fără dependințe.
//
// De ce în cod și nu prin PostGIS: coordonatele Stereo 70 se afișează în browser la fiecare mișcare
// a cursorului / pe fiecare punct identificat (REQ 1.5) — un round-trip la server per eveniment e
// exclus. Formulele sunt cele oficiale, iar rezultatul e verificat în teste contra `ST_Transform`
// (tests/stereo70.test.ts, toleranță 0,5 m; în practică diferența e sub 1 mm).
//
// Lanțul (identic cu pipeline-ul PROJ pe care îl folosesc ST_Transform și ogr2ogr la import):
//   WGS84 (lng, lat, h=0) → cartezian → Helmert 7 parametri INVERS → cartezian pe Krasovski 1940
//   → geodezic → proiecția stereografică oblică (dublă, pe sfera conformă Gauss) → (E, N).
//
// ⚠️ Transformarea de datum: EPSG „Pulkovo 1942(58) to WGS 84 (19)" — ANCPI, 3 m nominal —
// cu rotațiile în convenția **coordinate frame** (EPSG 9607). E ceea ce aplică PROJ ≥ 6 când i se
// dau codurile EPSG (deci ST_Transform și importul GDAL). String-ul istoric `+towgs84=…` din
// `spatial_ref_sys.proj4text` are ACELEAȘI numere, dar PROJ le-ar citi în convenția position
// vector → o deplasare de ~37 m față de datele importate. De-aceea semnele rotațiilor de mai jos
// sunt cele pentru coordinate frame, nu cele „din proj4text".
//
// Convenția de axe: în notația cadastrală românească X = NORD, Y = EST (invers față de
// convenția GIS x=est). Ca să nu existe confuzie, API-ul de aici vorbește în `east`/`north`;
// `format_stereo70` redă „X (N) / Y (E)" așa cum apar în actele ANCPI.

/** Coordonate plane Stereo 70 (EPSG:3844), în metri. */
export interface Stereo70 {
	east: number // Y în notația ANCPI
	north: number // X în notația ANCPI
}

/** Coordonate geografice WGS84 (EPSG:4326), în grade. Ordinea GeoJSON: lng, lat. */
export interface Wgs84 {
	lng: number
	lat: number
}

// ─────────────────────────────────────────────────────────────
// Parametri oficiali
// ─────────────────────────────────────────────────────────────

/** WGS 84 (EPSG:7030). */
const WGS84_A = 6378137
const WGS84_F = 1 / 298.257223563
/** Krasovski 1940 (EPSG:7024) — elipsoidul sistemului Stereo 70. */
const KRASS_A = 6378245
const KRASS_F = 1 / 298.3

/** Stereo 70 (EPSG:3844 / EPSG:19926): stereografic oblic, origine 46°N 25°E, k0, offset-uri. */
const LAT0 = (46 * Math.PI) / 180
const LNG0 = (25 * Math.PI) / 180
const K0 = 0.99975
const FALSE_EASTING = 500_000
const FALSE_NORTHING = 500_000

/**
 * Helmert Pulkovo 1942(58) → WGS 84 (EPSG:15995 „(19)", Romania, 3 m), convenție coordinate frame.
 * Translații în metri, rotații în secunde de arc, scara în ppm.
 */
const HELMERT = {
	tx: 2.329,
	ty: -147.042,
	tz: -92.08,
	rx: 0.309,
	ry: -0.325,
	rz: -0.497,
	ds: 5.69,
}
const ARCSEC = Math.PI / (180 * 3600)

// ─────────────────────────────────────────────────────────────
// Geodezie de bază: geodezic ↔ cartezian, Helmert
// ─────────────────────────────────────────────────────────────

interface Ellipsoid {
	a: number
	e2: number
}
const WGS84_ELL: Ellipsoid = { a: WGS84_A, e2: 2 * WGS84_F - WGS84_F * WGS84_F }
const KRASS_ELL: Ellipsoid = { a: KRASS_A, e2: 2 * KRASS_F - KRASS_F * KRASS_F }

type Xyz = readonly [number, number, number]

/** (φ, λ) radiani, h=0 → cartezian geocentric. */
function geodetic_to_xyz(ell: Ellipsoid, phi: number, lam: number): Xyz {
	const sin_phi = Math.sin(phi)
	const nu = ell.a / Math.sqrt(1 - ell.e2 * sin_phi * sin_phi)
	return [
		nu * Math.cos(phi) * Math.cos(lam),
		nu * Math.cos(phi) * Math.sin(lam),
		nu * (1 - ell.e2) * sin_phi,
	]
}

/** Cartezian geocentric → (φ, λ) radiani (înălțimea se ignoră — nu intră în proiecție). */
function xyz_to_geodetic(ell: Ellipsoid, [x, y, z]: Xyz): { phi: number; lam: number } {
	const p = Math.hypot(x, y)
	const lam = Math.atan2(y, x)
	// Iterație pe latitudine (converge în câțiva pași pentru h de ordinul sutelor de metri).
	let phi = Math.atan2(z, p * (1 - ell.e2))
	for (let i = 0; i < 10; i++) {
		const sin_phi = Math.sin(phi)
		const nu = ell.a / Math.sqrt(1 - ell.e2 * sin_phi * sin_phi)
		const h = p / Math.cos(phi) - nu
		const next = Math.atan2(z, p * (1 - (ell.e2 * nu) / (nu + h)))
		if (Math.abs(next - phi) < 1e-14) {
			phi = next
			break
		}
		phi = next
	}
	return { phi, lam }
}

/**
 * Helmert 7 parametri, convenția coordinate frame (EPSG 9607), sensul Pulkovo → WGS84:
 *   X' = T + (1+ds) · R · X,  R = [[1, rz, -ry], [-rz, 1, rx], [ry, -rx, 1]] (unghiuri mici).
 */
function helmert_forward([x, y, z]: Xyz): Xyz {
	const rx = HELMERT.rx * ARCSEC
	const ry = HELMERT.ry * ARCSEC
	const rz = HELMERT.rz * ARCSEC
	const s = 1 + HELMERT.ds * 1e-6
	return [
		HELMERT.tx + s * (x + rz * y - ry * z),
		HELMERT.ty + s * (-rz * x + y + rx * z),
		HELMERT.tz + s * (ry * x - rx * y + z),
	]
}

/** Inversul: WGS84 → Pulkovo. Ca în PROJ: scoate T, împarte la scară, aplică Rᵀ (unghiuri mici). */
function helmert_inverse([x, y, z]: Xyz): Xyz {
	const rx = HELMERT.rx * ARCSEC
	const ry = HELMERT.ry * ARCSEC
	const rz = HELMERT.rz * ARCSEC
	const s = 1 + HELMERT.ds * 1e-6
	const dx = (x - HELMERT.tx) / s
	const dy = (y - HELMERT.ty) / s
	const dz = (z - HELMERT.tz) / s
	return [dx - rz * dy + ry * dz, rz * dx + dy - rx * dz, -ry * dx + rx * dy + dz]
}

// ─────────────────────────────────────────────────────────────
// Stereografica oblică (EPSG 9809) pe sfera conformă Gauss — ca `+proj=sterea` din PROJ
// ─────────────────────────────────────────────────────────────

const E2 = KRASS_ELL.e2
const E = Math.sqrt(E2)

/** ((1 − e·sinφ)/(1 + e·sinφ))^exp — termenul elipsoidal al latitudinii conforme. */
function srat(e_sin: number, exp: number): number {
	return Math.pow((1 - e_sin) / (1 + e_sin), exp)
}

/** Constantele sferei conforme în punctul de origine (calculate o singură dată). */
const GAUSS = (() => {
	const sin0 = Math.sin(LAT0)
	const cos0 = Math.cos(LAT0)
	// Raza sferei conforme, în metri: sqrt(ρ0·ν0).
	const radius = (KRASS_A * Math.sqrt(1 - E2)) / (1 - E2 * sin0 * sin0)
	const n = Math.sqrt(1 + (E2 * cos0 ** 4) / (1 - E2))
	const chi0 = Math.asin(sin0 / n)
	const ratexp = 0.5 * n * E
	const k =
		Math.tan(0.5 * chi0 + Math.PI / 4) /
		(Math.pow(Math.tan(0.5 * LAT0 + Math.PI / 4), n) * srat(E * sin0, ratexp))
	return { radius, n, chi0, ratexp, k, sin_chi0: Math.sin(chi0), cos_chi0: Math.cos(chi0) }
})()

/** Elipsoid → sfera conformă: latitudinea conformă χ și longitudinea scalată Λ (rad, rel. la origine). */
function gauss_forward(phi: number, lam_rel: number): { chi: number; lam: number } {
	const chi =
		2 *
			Math.atan(
				GAUSS.k *
					Math.pow(Math.tan(0.5 * phi + Math.PI / 4), GAUSS.n) *
					srat(E * Math.sin(phi), GAUSS.ratexp),
			) -
		Math.PI / 2
	return { chi, lam: GAUSS.n * lam_rel }
}

/** Sfera conformă → elipsoid (iterație pe φ, converge rapid). */
function gauss_inverse(chi: number, lam: number): { phi: number; lam_rel: number } {
	const num = Math.pow(Math.tan(0.5 * chi + Math.PI / 4) / GAUSS.k, 1 / GAUSS.n)
	let phi = chi
	for (let i = 0; i < 20; i++) {
		const next = 2 * Math.atan(num * srat(E * Math.sin(phi), -0.5 * E)) - Math.PI / 2
		if (Math.abs(next - phi) < 1e-14) {
			phi = next
			break
		}
		phi = next
	}
	return { phi, lam_rel: lam / GAUSS.n }
}

/** Proiecția stereografică pe sferă (rază `radius`, scară k0) → (E, N) în metri, cu offset-uri. */
function sphere_forward(chi: number, lam: number): Stereo70 {
	const sin_chi = Math.sin(chi)
	const cos_chi = Math.cos(chi)
	const cos_lam = Math.cos(lam)
	const b = 1 + GAUSS.sin_chi0 * sin_chi + GAUSS.cos_chi0 * cos_chi * cos_lam
	const k = (2 * GAUSS.radius * K0) / b
	return {
		east: FALSE_EASTING + k * cos_chi * Math.sin(lam),
		north: FALSE_NORTHING + k * (GAUSS.cos_chi0 * sin_chi - GAUSS.sin_chi0 * cos_chi * cos_lam),
	}
}

/** Inversa proiecției pe sferă: (E, N) → (χ, Λ). */
function sphere_inverse(east: number, north: number): { chi: number; lam: number } {
	const x = (east - FALSE_EASTING) / K0
	const y = (north - FALSE_NORTHING) / K0
	const rho = Math.hypot(x, y)
	if (rho === 0) {
		return { chi: GAUSS.chi0, lam: 0 }
	}
	const c = 2 * Math.atan2(rho, 2 * GAUSS.radius)
	const sin_c = Math.sin(c)
	const cos_c = Math.cos(c)
	const chi = Math.asin(cos_c * GAUSS.sin_chi0 + (y * sin_c * GAUSS.cos_chi0) / rho)
	const lam = Math.atan2(x * sin_c, rho * GAUSS.cos_chi0 * cos_c - y * GAUSS.sin_chi0 * sin_c)
	return { chi, lam }
}

// ─────────────────────────────────────────────────────────────
// API public
// ─────────────────────────────────────────────────────────────

const DEG = Math.PI / 180

/** WGS84 (grade, ordinea GeoJSON lng/lat) → Stereo 70 (metri). Intrare nefinită → NaN pe ambele axe. */
export function wgs84_to_stereo70(lng: number, lat: number): Stereo70 {
	if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
		return { east: NaN, north: NaN }
	}
	const xyz_wgs = geodetic_to_xyz(WGS84_ELL, lat * DEG, lng * DEG)
	const { phi, lam } = xyz_to_geodetic(KRASS_ELL, helmert_inverse(xyz_wgs))
	const g = gauss_forward(phi, lam - LNG0)
	return sphere_forward(g.chi, g.lam)
}

/** Stereo 70 (metri, east/north) → WGS84 (grade). Intrare nefinită → NaN. */
export function stereo70_to_wgs84(east: number, north: number): Wgs84 {
	if (!Number.isFinite(east) || !Number.isFinite(north)) {
		return { lng: NaN, lat: NaN }
	}
	const s = sphere_inverse(east, north)
	const { phi, lam_rel } = gauss_inverse(s.chi, s.lam)
	const xyz_krass = geodetic_to_xyz(KRASS_ELL, phi, lam_rel + LNG0)
	const w = xyz_to_geodetic(WGS84_ELL, helmert_forward(xyz_krass))
	return { lng: w.lam / DEG, lat: w.phi / DEG }
}

/**
 * Anvelopa plauzibilă a României în Stereo 70 (cu marjă) — validarea unei intrări tastate
 * („du-mă la X/Y"): o pereche în afara ei e aproape sigur inversată sau într-un alt sistem.
 */
export const STEREO70_BOUNDS = Object.freeze({
	east_min: 110_000,
	east_max: 940_000,
	north_min: 210_000,
	north_max: 790_000,
})

export function stereo70_in_bounds(p: Stereo70): boolean {
	return (
		Number.isFinite(p.east) &&
		Number.isFinite(p.north) &&
		p.east >= STEREO70_BOUNDS.east_min &&
		p.east <= STEREO70_BOUNDS.east_max &&
		p.north >= STEREO70_BOUNDS.north_min &&
		p.north <= STEREO70_BOUNDS.north_max
	)
}

/**
 * Redare în notația cadastrală: „X = <nord> Y = <est>" (fără grupare de mii — se copiază direct în
 * CAD/inventare de coordonate; punctul zecimal e cel acceptat de toate programele GIS).
 * Ex.: `X = 500031.31  Y = 500120.37`.
 */
export function format_stereo70(p: Stereo70, decimals = 2): string {
	if (!Number.isFinite(p.east) || !Number.isFinite(p.north)) {
		return ""
	}
	return `X = ${p.north.toFixed(decimals)}  Y = ${p.east.toFixed(decimals)}`
}

/**
 * Parsează o pereche Stereo 70 tastată de utilizator. Acceptă etichete (`X=… Y=…`, `N … E …`),
 * separatori spațiu/virgulă/punct-și-virgulă/tab și virgula zecimală românească („500031,31").
 * FĂRĂ etichete, ordinea e cea din inventarele ANCPI: **X (nord) primul, Y (est) al doilea**.
 * `null` = nu s-au găsit exact două numere.
 */
export function parse_stereo70(text: string): Stereo70 | null {
	const s = text.trim()
	if (!s) {
		return null
	}
	// Etichete: X/N → nord, Y/E → est (case-insensitive), urmate de „=", „:" sau spațiu.
	const labeled: Partial<Stereo70> = {}
	const label_re = /\b([XYNE])\s*[=:]?\s*(-?\d+(?:[.,]\d+)?)/gi
	let m: RegExpExecArray | null
	while ((m = label_re.exec(s)) !== null) {
		const key = m[1]!.toUpperCase()
		const val = Number(m[2]!.replace(",", "."))
		if (key === "X" || key === "N") {
			labeled.north = val
		} else {
			labeled.east = val
		}
	}
	if (labeled.north !== undefined && labeled.east !== undefined) {
		return { east: labeled.east, north: labeled.north }
	}
	// Fără etichete: două numere. Virgula e zecimală doar când NU e urmată de spațiu
	// („500031,31 500120,37"); „500031, 500120" rămâne separator.
	const nums = s
		.replace(/(\d),(\d)/g, "$1.$2")
		.split(/[\s;,]+/)
		.filter((t) => t !== "")
		.map(Number)
	if (nums.length !== 2 || nums.some((n) => !Number.isFinite(n))) {
		return null
	}
	return { east: nums[1]!, north: nums[0]! }
}
